import {Graphics, Polygon} from "pixi.js";
import {inches} from "../../const";
import {glyphStyle, hasCategory, HardwareGlyph, hingeEdgeX, strikeEdgeX} from "./HardwareGlyph";

// a rim exit device seen from the push side: a rounded latch chassis centred on the hub
// (the placement point the dimensions measure to) with the rim latch bolt reaching from
// the chassis past the strike edge, and a rail running from the chassis across the door
// to short of the hinge edge. The push pad sits on the rail over its outer part, leaving
// the bare rail toward the hinge end. All sizes are typical catalogue values (scene
// units = mm); the rail and pad scale with the door width.
const CHASSIS_LENGTH = 3 * inches;
const CHASSIS_HEIGHT = 7 * inches;
const CHASSIS_RADIUS = 0.5 * inches;
const RAIL_HEIGHT = 2 * inches;
const PAD_HEIGHT = 3 * inches;
const PAD_GAP = 0.5 * inches;               // chassis to the start of the pad
const PAD_SHARE = 0.7;                     // share of the rail the pad covers
const HINGE_EDGE_GAP = 3 * inches;          // rail end to the hinge edge
const LATCH_PROJECTION = 0.625 * inches;    // bolt past the strike edge
const LATCH_HEIGHT = 1 * inches;
const CYLINDER_RADIUS = 0.5 * inches;
const OUTLINE = 0.25 * inches;
const HIT_PAD = 1 * inches;

export const rimExitDevice: HardwareGlyph = {
    id: "rimExitDevice",
    targets: ["door"],
    matches: item => hasCategory(item, "Rim Exit Device", "Rim Exit Device & Trim"),
    draw(g: Graphics, ctx): Polygon {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const cy = ctx.hub.y;
        const strike = strikeEdgeX(ctx);
        // `dir` points from the strike edge toward the hinge edge.
        const dir = strike.dir;
        const hingeX = hingeEdgeX(ctx);

        const chassisStart = ctx.hub.x - dir * CHASSIS_LENGTH / 2;
        const chassisEnd = ctx.hub.x + dir * CHASSIS_LENGTH / 2;
        const railEnd = hingeX - dir * HINGE_EDGE_GAP;
        const railLength = Math.max(0, Math.abs(railEnd - chassisEnd));
        const padStart = chassisEnd + dir * PAD_GAP;
        const padEnd = padStart + dir * Math.max(0, railLength * PAD_SHARE - PAD_GAP);

        // rail: outlined, lightly filled, chassis to the hinge end.
        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.2);
        rect(g, chassisEnd, railEnd, cy - RAIL_HEIGHT / 2, RAIL_HEIGHT);
        g.endFill();

        // push pad: solid, on the rail's outer part.
        g.lineStyle(0);
        g.beginFill(color, alpha);
        rect(g, padStart, padEnd, cy - PAD_HEIGHT / 2, PAD_HEIGHT);
        g.endFill();

        // chassis: rounded, lightly filled, over the rail end.
        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.35);
        g.drawRoundedRect(Math.min(chassisStart, chassisEnd), cy - CHASSIS_HEIGHT / 2, CHASSIS_LENGTH, CHASSIS_HEIGHT, CHASSIS_RADIUS);
        g.endFill();

        // rim latch bolt: from the chassis face past the strike edge.
        g.lineStyle(0);
        g.beginFill(color, alpha);
        rect(g, chassisStart, strike.x - dir * LATCH_PROJECTION, cy - LATCH_HEIGHT / 2, LATCH_HEIGHT);
        g.endFill();

        // cylinder at the hub so the placement point (the prep centre) stays visible.
        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(0, 0);
        g.drawCircle(ctx.hub.x, cy, CYLINDER_RADIUS);
        g.endFill();

        // hit area follows the outline: chassis-tall at the strike end (latch bolt included),
        // pad-tall along the rail.
        const latchX = strike.x - dir * LATCH_PROJECTION;
        const chassisHalf = CHASSIS_HEIGHT / 2 + HIT_PAD;
        const railHalf = PAD_HEIGHT / 2 + HIT_PAD;
        const x0 = latchX - dir * HIT_PAD;
        const x1 = chassisEnd + dir * HIT_PAD;
        const x2 = railEnd + dir * HIT_PAD;
        return new Polygon([
            x0, cy - chassisHalf,
            x1, cy - chassisHalf,
            x1, cy - railHalf,
            x2, cy - railHalf,
            x2, cy + railHalf,
            x1, cy + railHalf,
            x1, cy + chassisHalf,
            x0, cy + chassisHalf,
        ]);
    },
};

// a rect between two x positions given in either order.
function rect(g: Graphics, xa: number, xb: number, y: number, h: number) {
    g.drawRect(Math.min(xa, xb), y, Math.abs(xb - xa), h);
}
